import { useState } from "react";
import { Link2, Pencil, Copy } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { waUrl } from "@/lib/contacts";
import { twilioFor, sendWa } from "@/lib/whatsapp";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { niceName, partyName, isFeminine } from "@/lib/format";
import { BuyerCpcvData, TraitFields, TraitTags, traitsOf } from "@/components/BuyerData";
import { PersonLine } from "@/components/PersonLine";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ROLE_KEY = { vendedor: "sellers", comprador: "buyers" };
const closedOf = (p) => ["escriturado", "sem_venda"].includes(p.status);
const copy = async (text, msg) => { await navigator.clipboard.writeText(text).catch(() => {}); toast.success(msg); };

// Língua das mensagens e da página do cliente; só mostra. Ponto = sugerida ou escolhida pelo cliente.
function LangTag({ lk }) {
  const note = lk.lang_by_client ? `Escolhida pelo cliente na página (${lk.lang === "en" ? "inglês" : "português"})`
    : lk.lang_auto && lk.lang === "en" ? `Sugerido inglês: nacionalidade ${lk.lang_auto}` : null;
  const tip = note || `Mensagens e página do cliente em ${lk.lang === "en" ? "inglês" : "português"}`;
  return (
    <button type="button" onClick={() => toast(tip)} title={tip} data-testid={`party-lang-${lk.role}-${lk.party_index}`}
      className="relative inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-bold tracking-wide">
      {lk.lang === "en" ? "EN" : "PT"}
      {note && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-500 ring-2 ring-white" data-testid={`party-lang-dot-${lk.role}-${lk.party_index}`} />}
    </button>
  );
}

// Campo inline «Adicionar telefone / email»; save(v) grava.
function AddInline({ kind, save, onClose, testid }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); try { await save(v); onClose(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } setBusy(false); };
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2" data-testid={testid}>
      <input autoFocus type={kind === "phone" ? "tel" : "email"} value={v} onChange={(e) => setV(e.target.value)} placeholder={kind === "phone" ? "912 345 678" : "nome@exemplo.pt"}
        className="w-48 h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid={`${testid}-input`} />
      <button onClick={go} disabled={busy || !v.trim()} className="h-9 px-3 rounded-lg text-sm font-semibold bg-ec-green text-white disabled:opacity-50" data-testid={`${testid}-save`}>Guardar</button>
      <button onClick={onClose} className="h-9 text-sm text-slate-500">Cancelar</button>
    </div>
  );
}

function roleLabel(role, party, stage) {
  const sale = stage && stage !== "angariacao";
  if (party?.representative || /,\s*(lda|s\.a|unipessoal)/i.test(partyName(party) || "")) return role === "vendedor" ? `Proprietário${sale ? " (vendedor)" : ""}` : "Comprador";
  const f = isFeminine(party);
  if (role === "vendedor") return `${f ? "Proprietária" : "Proprietário"}${sale ? (f ? " (vendedora)" : " (vendedor)") : ""}`;
  return f ? "Compradora" : "Comprador";
}

const honor = (pt) => { if (pt?.title) return pt.title === "none" ? "" : `${pt.title} `; const g = pt?.gender || pt?.gender_manual; return g === "F" ? "Sra. " : g === "M" ? "Sr. " : ""; };
const TITLES = [["Sr.", "Sr."], ["Sra.", "Sra."], ["Dr.", "Dr."], ["Dra.", "Dra."], ["none", "Só o nome"]];
const inp = "w-full h-10 rounded-lg border border-slate-200 px-3 text-[15px] disabled:bg-slate-50 disabled:text-slate-500";
const Lbl = ({ t, children }) => <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">{t}</span>{children}</label>;

// ✎ de cada pessoa: contacto (nome, telefone, email, língua), tratamento, características e (comprador) dados para o CPCV. Grava com «Guardar».
function PersonDialog({ p, role, idx, lk, onClose, onDone }) {
  const { user } = useAuth();
  const side = ROLE_KEY[role];
  const party = (p[side] || [])[idx] || {};
  const rep = party.representative && validRep(party);
  const init = {
    name: party.display_name || partyName(party) || "", phone: (lk ? lk.phone : rep ? party.rep_phone : party.phone) || "",
    email: (lk ? lk.email : rep ? party.rep_email : party.email) || "", lang: lk?.lang === "en" ? "en" : "pt", title: party.title || "",
    traits: { company: false, foreign: false, proxy: false, ...traitsOf(p, side, idx) },
  };
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const canName = ["gestor", "direccao"].includes(user?.role);
  const g = party.gender || party.gender_manual;
  const ccTitle = g === "F" ? "Sra. (do cartão de cidadão)" : g === "M" ? "Sr. (do cartão de cidadão)" : "Do cartão de cidadão";
  const titles = TITLES.some(([k]) => k === init.title) || !init.title ? TITLES : [...TITLES, [init.title, init.title]];
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    const base = `/processes/${p.id}/parties/${side}/${idx}`;
    try {
      if (canName && f.name.trim() !== init.name.trim()) await api.put(`${base}/display-name`, { display_name: f.name.trim() });
      if (f.title !== init.title) await api.put(`${base}/display-name`, { title: f.title });
      const c = Object.fromEntries(["phone", "email"].filter((k) => f[k].trim() && f[k].trim() !== init[k].trim()).map((k) => [k, f[k].trim()]));
      if (Object.keys(c).length) await api.put(`${base}/contact`, c);
      if (lk && f.lang !== init.lang) await api.put(`/processes/${p.id}/portal-links/${lk.token}/lang`, { lang: f.lang });
      const t = Object.fromEntries(Object.entries(f.traits).filter(([k, v]) => v !== init.traits[k]));
      if (Object.keys(t).length) await api.put(`${base}/traits`, t);
      toast.success("✓ Guardado");
      onDone();
      onClose();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-md max-h-[88vh] overflow-y-auto p-0 gap-0" data-testid={`person-dialog-${role}-${idx}`}>
        <DialogHeader className="px-6 pt-6 pb-3"><DialogTitle className="font-heading navy-text">Editar pessoa</DialogTitle></DialogHeader>
        <div className="px-6 space-y-4">
          <Lbl t="Nome completo"><input className={inp} value={f.name} onChange={set("name")} disabled={!canName} title={canName ? undefined : "Só a gestão pode corrigir o nome"} data-testid="pd-name" /></Lbl>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Lbl t="Telefone"><input className={inp} type="tel" value={f.phone} onChange={set("phone")} placeholder="912 345 678" data-testid="pd-phone" /></Lbl>
            <Lbl t="Email"><input className={inp} type="email" value={f.email} onChange={set("email")} placeholder="nome@exemplo.pt" data-testid="pd-email" /></Lbl>
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Lbl t="Tratamento">
              <select value={f.title} onChange={set("title")} className={`${inp} bg-white text-sm px-2`} data-testid={`treat-${role}-${idx}`}>
                <option value="">{ccTitle}</option>
                {titles.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Lbl>
            {lk && <Lbl t="Língua">
              <div className="flex h-10 w-28 rounded-lg border border-slate-200 p-0.5" data-testid="pd-lang">
                {[["pt", "PT"], ["en", "EN"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setF((o) => ({ ...o, lang: k }))} aria-pressed={f.lang === k} data-testid={`pd-lang-${k}`}
                    className={`flex-1 rounded-md text-sm font-semibold transition-colors ${f.lang === k ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{l}</button>
                ))}
              </div>
            </Lbl>}
          </div>
          <div className="border-t border-slate-100 pt-4"><TraitFields value={f.traits} onChange={(traits) => setF((o) => ({ ...o, traits }))} side={side} idx={idx} /></div>
          {role === "comprador" && <BuyerCpcvData p={p} idx={idx} onDone={onDone} ro={false} />}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 mt-5 px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm text-slate-600 hover:bg-slate-50" data-testid="pd-cancel">Cancelar</button>
          <button onClick={save} disabled={busy} className="h-10 px-5 rounded-lg text-sm font-semibold bg-ec-green text-white disabled:opacity-50" data-testid="pd-save">Guardar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
const validRep = (pt) => !!(pt.rep_phone || pt.rep_email);

// Empresa: «Sr. Joaquim · gerente»; sem gerente, «Indicar gerente →».
function RepLine({ pid, role, idx, party, onDone, ro }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  if (!party?.company) return null;
  if (party.rep_call) return <div className="text-sm text-slate-700" data-testid={`party-rep-${role}-${idx}`}>{party.rep_call} · gerente</div>;
  const save = async (treat) => {
    try { await api.put(`/processes/${pid}/parties/${ROLE_KEY[role]}/${idx}/representative`, { name, treat }); toast.success("Gerente indicado"); setOpen(false); onDone?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const ok = name.trim().length >= 3;
  const chip = "h-9 px-3 text-sm rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40";
  return (
    <div id={`sec-gerente-${role}-${idx}`} className="rounded-lg scroll-mt-24 transition-shadow">
      {ro ? <div className="text-sm text-slate-500" data-testid={`party-rep-missing-${role}-${idx}`}>Gerente por indicar</div>
        : <button onClick={() => setOpen(!open)} className="text-sm font-medium text-ec-green hover:underline" data-testid={`party-rep-missing-${role}-${idx}`}>+ Indicar gerente</button>}
      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`party-rep-form-${role}-${idx}`}>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome do gerente" className="w-48 h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid={`party-rep-name-${role}-${idx}`} />
          <button disabled={!ok} onClick={() => save("sr")} className={chip} data-testid={`party-rep-sr-${role}-${idx}`}>Sr.</button>
          <button disabled={!ok} onClick={() => save("sra")} className={chip} data-testid={`party-rep-sra-${role}-${idx}`}>Sra.</button>
        </div>
      )}
    </div>
  );
}

function ClientRow({ p, role, idx, lk, testid, onDone }) {
  const ro = useAuth().user?.role === "direccao";
  const closed = closedOf(p);
  const [add, setAdd] = useState(null);
  const [edit, setEdit] = useState(false);
  const parties = p[ROLE_KEY[role]] || [];
  const pt = parties[idx] || {};
  const rep = pt.representative && (pt.rep_phone || pt.rep_email);
  const phone = lk ? lk.phone : rep ? pt.rep_phone : pt.phone;
  const email = lk ? lk.email : rep ? pt.rep_email : pt.email;
  const name = `${honor(pt)}${partyName(pt) || niceName(lk?.name) || "—"}`;
  const send = async (channel) => {
    const tw = channel === "whatsapp" && twilioFor(p.id);
    const win = channel === "whatsapp" && !tw ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post(`/processes/${p.id}/portal-links/${lk.token}/send`, { channel, base_url: window.location.origin });
      if (tw) await sendWa(p.id, lk.phone, data.wa_text);
      if (win) win.location.href = `${waUrl(lk.phone)}?text=${encodeURIComponent(data.wa_text)}`;
      if (channel === "copy") { await navigator.clipboard.writeText(data.url).catch(() => {}); toast.success("Link da página do cliente copiado"); }
      if (channel === "email") toast.success(`Página enviada por email a ${lk.name}`);
      onDone?.();
    } catch (e) { if (win) win.close(); toast.error(apiErr(e.response?.data?.detail)); }
  };
  const canAdd = !ro && !closed;
  const saveContact = (kind) => async (v) => { await api.put(`/processes/${p.id}/parties/${ROLE_KEY[role]}/${idx}/contact`, { [kind]: v }); toast.success(kind === "phone" ? "Telefone adicionado" : "Email adicionado"); onDone?.(); };
  const sub = roleLabel(role, pt, p.stage);
  return (
    <>
      <PersonLine testid={testid} role={sub} phone={phone} email={email}
        title={<>{name}{!ro && !closed && <button onClick={() => setEdit(true)} className="text-slate-400 hover:text-slate-700" aria-label="Editar pessoa" data-testid={`party-edit-${role}-${idx}`}><Pencil size={13} /></button>}
          {lk && <LangTag lk={lk} />}<TraitTags p={p} side={ROLE_KEY[role]} idx={idx} /></>}
        onWa={lk && !closed && !twilioFor(p.id) ? () => send("whatsapp") : undefined} onMail={lk && !closed ? () => send("email") : undefined}
        onAddPhone={canAdd ? () => setAdd("phone") : undefined} onAddEmail={canAdd ? () => setAdd("email") : undefined}
        more={lk && !closed ? [...(twilioFor(p.id) ? [{ label: "Enviar link por WhatsApp", icon: Link2, onClick: () => send("whatsapp"), testid: `party-walink-${lk.token}` }] : []),
          { label: "Copiar link do portal", icon: Link2, onClick: () => send("copy"), testid: `party-copy-${lk.token}` }] : []}
        extra={<>
          <RepLine pid={p.id} role={role} idx={idx} party={pt} onDone={onDone} ro={ro || closed} />
          {add && <AddInline kind={add} save={saveContact(add)} onClose={() => setAdd(null)} testid={`party-add-${add}-${role}-${idx}`} />}
        </>} />
      {edit && <PersonDialog p={p} role={role} idx={idx} lk={lk} onClose={() => setEdit(false)} onDone={onDone} />}
    </>
  );
}

export function Party({ role, links, p, onDone }) {
  const parties = p[ROLE_KEY[role]] || [];
  return parties.map((pt, i) => {
    if (!partyName(pt)) return null;
    const lk = links.find((l) => l.role === role && (l.party_index ?? 0) === i);
    return <ClientRow key={lk?.token || i} p={p} role={role} idx={i} lk={lk} onDone={onDone} testid={`party-${role}${i ? `-${i}` : ""}`} />;
  });
}

// Consultor e gestão processual: mesmo formato; o próprio ou a direção podem acrescentar o telefone.
function StaffRow({ testid, uid, name, tag, role, phone, email, onDone }) {
  const { user } = useAuth();
  const [add, setAdd] = useState(false);
  const self = user?.id && uid && String(user.id) === String(uid);
  const can = self || user?.role === "direccao";
  const save = async (v) => {
    await (self ? api.put("/me/profile", { phone: v.trim() }) : api.patch(`/users/${uid}`, { phone: v.trim() }));
    toast.success("Telefone adicionado"); onDone?.();
  };
  const line = [name, phone && `· ${phone}`, email && `· ${email}`].filter(Boolean).join(" ");
  return (
    <PersonLine testid={testid} title={<>{name}{tag}</>} role={role} phone={phone} email={email}
      onAddPhone={can ? () => setAdd(true) : undefined}
      more={[{ label: "Copiar contacto", icon: Copy, onClick: () => copy(line, "Contacto copiado"), testid: `${testid}-copy` }]}
      extra={add && <AddInline kind="phone" save={save} onClose={() => setAdd(false)} testid={`${testid}-add-phone-form`} />} />
  );
}

export function StaffRows({ p, onDone }) {
  const g = p.gestao;
  return (
    <>
      <StaffRow testid="party-consultor" uid={p.consultant_id} name={niceName(p.consultant_name) || "—"} role="Consultor responsável"
        phone={p.consultant_phone} email={p.consultant_email} onDone={onDone} />
      {g && <StaffRow testid="party-gestor" uid={g.id} name={g.name} role={null}
        tag={<span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wide" data-testid="party-gestor-tag">Gestão processual</span>}
        phone={g.phone} email={g.email} onDone={onDone} />}
    </>
  );
}
